
import React, { useEffect, useState } from "react";
import { fetchAdminUsers, isAdmin } from "@/utils/admin";
import { useAuth } from "@/providers/AuthProvider";
import AdminLayout from "@/components/AdminLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { UserIcon, Users, Trophy, Award } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface AdminUserRow {
  user_id: string;
  role: string;
  created_at: string;
}

interface UserSubscription {
  user_id: string;
  plan_name: string;
  status: string;
  is_permanent: boolean;
}

const AdminUsersPage: React.FC = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<AdminUserRow[]>([]);
  const [subscriptions, setSubscriptions] = useState<Record<string, UserSubscription>>({});
  const [loading, setLoading] = useState(true);
  const [isAdminUser, setIsAdminUser] = useState(false);
  
  useEffect(() => {
    if (user) {
      isAdmin(user.id).then(admin => {
        setIsAdminUser(admin);
        if (admin) {
          fetchAdmins();
        }
      });
    }
  }, [user]);

  const fetchAdmins = async () => {
    setLoading(true);
    const { data, error } = await fetchAdminUsers();
    if (!error && data) {
      setAdmins(data);
      
      // Fetch subscriptions for all admin users
      if (data.length > 0) {
        const userIds = data.map(admin => admin.user_id);
        fetchSubscriptions(userIds);
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async (userIds: string[]) => {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select(`
        user_id,
        status,
        is_permanent,
        subscription_plan:subscription_plan_id (name)
      `)
      .in('user_id', userIds)
      .eq('status', 'active');
    
    if (!error && data) {
      const subscriptionMap: Record<string, UserSubscription> = {};
      data.forEach(sub => {
        subscriptionMap[sub.user_id] = {
          user_id: sub.user_id,
          plan_name: sub.subscription_plan?.name || 'Unknown Plan',
          status: sub.status,
          is_permanent: sub.is_permanent
        };
      });
      setSubscriptions(subscriptionMap);
    }
    
    setLoading(false);
  };

  if (!user) {
    return (
      <AdminLayout title="Administrator Access">
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
          <p className="text-gray-500">You must be logged in to access this page.</p>
        </div>
      </AdminLayout>
    );
  }

  if (!isAdminUser) {
    return (
      <AdminLayout title="Administrator Access">
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access this page.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Administrator Users">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="space-y-1">
              <CardTitle className="text-sm font-medium">
                Total Admins
              </CardTitle>
              <CardDescription>
                Number of administrators
              </CardDescription>
            </div>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? "..." : admins.length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="space-y-1">
              <CardTitle className="text-sm font-medium">
                Permanent Pro Plans
              </CardTitle>
              <CardDescription>
                Users with lifetime access
              </CardDescription>
            </div>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? "..." : Object.values(subscriptions).filter(sub => sub.is_permanent).length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Administrator Users</CardTitle>
          <CardDescription>
            List of all users with administrative privileges
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center">Loading administrators...</div>
          ) : admins.length === 0 ? (
            <div className="py-10 text-center">No administrators found.</div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User ID</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Plan Status</TableHead>
                    <TableHead>Created At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.user_id}>
                      <TableCell className="font-medium flex items-center">
                        <UserIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                        {admin.user_id}
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {admin.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        {subscriptions[admin.user_id] ? (
                          <div className="flex items-center">
                            {subscriptions[admin.user_id].is_permanent ? (
                              <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800">
                                <Trophy className="h-3 w-3" />
                                Permanent {subscriptions[admin.user_id].plan_name}
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                {subscriptions[admin.user_id].plan_name}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                            No active plan
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{new Date(admin.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminUsersPage;
